<script setup>
import { provide, onMounted, onBeforeUnmount } from 'vue';
import { CdrContainer, CdrText } from '@rei/cedar';
import { useCancellationStore } from '../../stores/day-cancellation';
import DayCancellationWizard from '../../components/day-cancellation/DayCancellationWizard.vue';
import ReservationLookup from '../../components/day-cancellation/ReservationLookup.vue';
import SelectReservations from '../../components/day-cancellation/SelectReservations.vue';
import ConfirmCancellation from '../../components/day-cancellation/ConfirmCancellation.vue';
import cms from '../../components/day-cancellation/data';
import { metricsWrapper as metrics, metricsOnGlobalClick } from '../../common/metrics';
import { setBeforeUnload, removeBeforeUnload } from '../../common/elements-helper';
import CancelledReservation from '../../components/day-cancellation/CancelledReservation.vue';
import CancellationSuccess from '../../components/day-cancellation/CancellationSuccess.vue';
import CancellationError from '../../components/day-cancellation/CancellationError.vue';

const props = defineProps({
  enableDayCancellation: {
    type: Boolean,
    default: true,
  },
});

const cancellation = useCancellationStore();

const steps = [
  {
    title: cms.reservationLookup.title,
    component: ReservationLookup,
  },
  {
    title: cms.selectReservations.title,
    component: SelectReservations,
  },
  {
    title: cms.confirmCancellation.title,
    component: ConfirmCancellation,
  },
];

const metricsClick = (componentName, identifier) => {
  metrics.link({ linkName: `day-cancellation:${componentName}_${identifier}` });
};

const onClick = (e) => {
  metricsOnGlobalClick(e);
};

provide('metricsClick', metricsClick);

onMounted(() => {
  metrics.view({
    pageName: 'opo:day_cancellation',
    siteId: 'local-experiences',
    templateType: 'cancellation',
  });

  if (props.enableDayCancellation) {
    setBeforeUnload();
  }
});

onBeforeUnmount(() => {
  if (props.enableDayCancellation) {
    removeBeforeUnload();
  }
});
</script>

<template>
  <CdrContainer
    class="day-cancellation"
    @click="onClick"
  >
    <template v-if="props.enableDayCancellation">
      <template v-if="cancellation.isCancelSuccess">
        <CancellationSuccess />
      </template>
      <template v-else-if="cancellation.isCancelError">
        <CancellationError />
      </template>
      <template v-else-if="cancellation.isCancelled">
        <CancelledReservation />
      </template>
      <template v-else>
        <div class="day-cancellation__header">
          <CdrText
            class="day-cancellation__title"
            tag="h1"
          >
            {{ cms.title }}
          </CdrText>
          <CdrText
            v-if="cancellation.isFirstStep"
            class="day-cancellation__body"
          >
            {{ cms.body }}
          </CdrText>
        </div>
        <DayCancellationWizard :steps="steps" />
      </template>
    </template>
    <template v-else>
      <CdrText
        class="day-cancellation__title"
        tag="h1"
      >
        {{ cms.disabled.title }}
      </CdrText>
      <CdrText class="day-cancellation__body">
        <span v-html="cms.disabled.body" />
      </CdrText>
    </template>
  </CdrContainer>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '@rei/cdr-component-variables/dist/scss/index.scss';

.day-cancellation {
  &__body {
    a {
      @include cdr-link-base-mixin;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.day-cancellation {
  padding-top: $cdr-space-four-x;
  padding-bottom: $cdr-space-four-x;

  &__title {
    @include cdr-text-heading-serif-900;
  }

  &__body {
    @include cdr-text-body-300;
    padding-top: $cdr-space-one-x;
  }
}
</style>
